import React, { useState } from 'react';
import { FiList, FiPaperclip } from 'react-icons/fi';
import { addMonths, differenceInCalendarDays, format, getDaysInMonth, parseISO, setDate, startOfDay } from 'date-fns';
import * as S from '../styles';
import ModalImg from './modal_img';
// ----------------------

export const getDataParcelaAtual = (financiamento, parcelasPagas) => {
  const primeiraParcela = financiamento?.primeiraParcela;
  const diaVencimento = Number(financiamento?.vencimento);
  const quantidadeParcelas = Number(financiamento?.n_Parcelas);

  if (!primeiraParcela || !Number.isInteger(diaVencimento) || diaVencimento < 1 || diaVencimento > 31
    || !Number.isInteger(quantidadeParcelas) || quantidadeParcelas < 1) {
    return null;
  }

  const dataInicial = primeiraParcela instanceof Date
    ? primeiraParcela
    : parseISO(String(primeiraParcela));

  if (Number.isNaN(dataInicial.getTime())) {
    return null;
  }

  const hoje = startOfDay(new Date());
  const primeiraData = startOfDay(dataInicial);
  // Com histórico de pagamentos, a parcela atual é a primeira ainda não paga
  const indiceMesAtual = Number.isInteger(parcelasPagas)
    ? parcelasPagas
    : (hoje.getFullYear() - primeiraData.getFullYear()) * 12 + hoje.getMonth() - primeiraData.getMonth();

  if (indiceMesAtual < 0) {
    return primeiraData;
  }

  if (indiceMesAtual >= quantidadeParcelas) {
    return null;
  }

  if (indiceMesAtual === 0) {
    return primeiraData;
  }

  const mesParcela = addMonths(primeiraData, indiceMesAtual);
  return setDate(mesParcela, Math.min(diaVencimento, getDaysInMonth(mesParcela)));
};
// ----------------------

export const isFinanciamentoAtrasado = (event) => {
  if (event?.tipo !== 'Financiamento') return false;

  const dataParcela = getDataParcelaAtual(event.financiamento, event.parcelasPagas);
  return !!dataParcela && dataParcela < startOfDay(new Date());
};
// ----------------------

const formatClienteNome = (nome) => {
  if (!nome) return 'Cliente não informado';

  const partes = String(nome).trim().split(/\s+/).filter(Boolean);

  if (partes.length <= 2) {
    return partes.join(' ');
  }

  return `${partes[0]} ${partes[partes.length - 1]}`;
};
// ----------------------

const formatPrimeiroNome = (nome) => {
  if (!nome) return '';

  return String(nome).trim().split(/\s+/)[0].toUpperCase();
};
// ----------------------

export const isEventoAtrasado = (event) => (
  isFinanciamentoAtrasado(event) || Number(event?.diasEvento) >= 90
);
// ----------------------

// Dias de atraso usados para ordenar a sequência de piscadas
export const getDiasAtraso = (event) => {
  if (event?.tipo === 'Financiamento') {
    const dataParcela = getDataParcelaAtual(event.financiamento, event.parcelasPagas);
    if (dataParcela) return differenceInCalendarDays(new Date(), dataParcela);
  }
  const dias = Number(event?.diasEvento);
  return Number.isFinite(dias) ? dias : 0;
};
// ----------------------

export default function EventCard({ event, accent, onClick, onTasksClick, blinkDelay = null, destaqueAtraso = false, mostrarMeses = false }) {
  const [arquivosAbertos, setArquivosAbertos] = useState(false);

  if (!event) {
    return null;
  }

  const arquivos = Array.isArray(event.arquivos)
    ? event.arquivos.filter((arquivo) => typeof arquivo === 'string' && arquivo.trim())
    : [];
  const dataParcela = event.tipo === 'Financiamento'
    ? getDataParcelaAtual(event.financiamento, event.parcelasPagas)
    : null;
  const atrasado = isFinanciamentoAtrasado(event);
  const eventoAntigo = Number(event.diasEvento) >= 90;
  const diasEvento = event.data && Number.isFinite(Number(event.diasEvento))
    && Number(event.diasEvento) >= 0
    ? Number(event.diasEvento)
    : null;
  const eventoAntigo90 = destaqueAtraso && Number(event.diasEvento) >= 90;
  const dataExibida = event.tipo === 'Financiamento'
    ? dataParcela ? `Parcela: ${format(dataParcela, 'dd/MM/yyyy')}` : 'Parcela: Data não informada'
    : event.data || 'Data não informada';
    
  // #############################################
  return (
    <S.Card accent={accent} overdue={atrasado || eventoAntigo} blinkDelay={blinkDelay} onClick={() => onClick?.(event)}>
      <S.CardHeader>
        {/* placa e modelo do veículo */}
        <S.CardCode>{event.placa || 'EV-000'} - {formatPrimeiroNome(event.modelo)}</S.CardCode>
        <S.CardActions>
          {arquivos.length > 0 && (
            <S.TaskButton
              type="button"
              accent={accent}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                setArquivosAbertos(true);
              }}
              aria-label={`Abrir arquivos salvos do evento ${event.placa || ''}`}
              title={`Arquivos salvos (${arquivos.length})`}
            >
              <FiPaperclip size={16} />
            </S.TaskButton>
          )}

          {/* Botão de tarefas do evento */}
          {Array.isArray(event.tarefas) && event.tarefas.length > 0 && (
            <S.TaskButton
              type="button"
              accent={accent}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                if (onTasksClick) onTasksClick(event);
              }}
              aria-label={`Abrir tarefas do evento ${event.placa || ''}`}
              title="Abrir tarefas do evento"
            >
              <FiList size={16} />
            </S.TaskButton>
          )}

          {/* Informação de status do evento */}
          <S.CardBadge
            accent={accent}
            statusAccent={
              event.status === 'Liberado'
                ? '#16a34a'
                : event.status === 'Pendente'
                  ? '#d6a503'
                  : undefined
            }
          >
            {event.status || 'Aberto'}
          </S.CardBadge>
        </S.CardActions>
      </S.CardHeader>

      {/* nome cliente e data do evento */}
      <S.CardTitle>{event.tipo || 'Evento sem nome'}</S.CardTitle>
      <S.CardMeta>{formatClienteNome(event.cliente)}</S.CardMeta>
      <S.CardMetaRow>
        <S.CardMeta>
          {dataExibida + " - "}
          <span style={eventoAntigo90 ? { color: '#c06f6f', fontWeight: 500 } : undefined}>
            {eventoAntigo90 && mostrarMeses && diasEvento != null
              ? `${Math.floor(diasEvento / 30)} meses`
              : diasEvento}
          </span>
        </S.CardMeta>
        {/* valor pago ou pendente */}
        <S.CardMeta style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span>{event.valorPago != null ? 'Pago:' : 'Pendente:'}</span>
          <span>
            {(event.valorPago != null
              ? Number(event.valorPago)
              : event.pendente != null
                ? parseFloat(event.pendente)
                : 0
            ).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </span>
        </S.CardMeta>
      </S.CardMetaRow>

      {/* botão de arquivos do evento */}
      <ModalImg
        aberto={arquivosAbertos}
        onFechar={() => setArquivosAbertos(false)}
        arquivos={arquivos}
        titulo={`Arquivos salvos - ${event.placa || 'Evento'}`}
        id={event.id}
      />
    </S.Card>
  );
}
